import {
  CtaButton,
  FormNotice,
  Heading,
  ModalBox,
  Spinner,
  SwitchToggle,
  TextInput,
  theme,
} from '@zappyrent/hundop';
import { debounce, toUpper, upperFirst } from 'lodash';
import { rgba } from 'polished';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import paymentsAPI from '../../../../services/api/payments';
import { BankAccountForm } from '../../../../shared/ts/payments';
import {
  StyledCheckIcon,
  StyledLabel,
  StyledModalFieldset,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalInnerWrapper,
  StyledModalFieldList,
  StyledModalFieldListItem,
  StyledModalSpinnerWrapper,
  StyledPaymentsListItem,
  StyledPaymentsListItemActive,
  StyledPaymentsListItemCta,
  StyledPaymentsListItemText,
  StyledError,
} from '../UserPayments.styles';
import { UserPaymentsBankAccountsItemProps } from './UserPaymentsBankAccounts.types';

const UserPaymentsBankAccountsItem = ({
  id,
  isDefault,
  IBAN,
  accountHolderName = '',
  bankName,
  country,
  onEditBankAccount,
}: UserPaymentsBankAccountsItemProps): JSX.Element => {
  const { t } = useTranslation(['a11y', 'form', 'payments', 'errors']);
  const { control, formState, handleSubmit, reset } = useForm<BankAccountForm>({
    defaultValues: {
      isDefault,
      accountHolderName,
    },
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);
  const [error, setError] = useState<string>();

  const handleFormSubmit = async ({
    accountHolderName: newAccountHolderName,
    isDefault: newIsDefault,
  }: BankAccountForm) => {
    if (
      accountHolderName === newAccountHolderName &&
      isDefault === newIsDefault
    ) {
      setIsModalOpen(false);
      return;
    }

    setIsSavingData(true);

    try {
      const newBankAccount = await paymentsAPI.updateBankAccount(
        id!,
        newAccountHolderName,
        newIsDefault,
      );

      onEditBankAccount();
      reset(newBankAccount);
      closeModal();
    } catch (err) {
      console.error(err);
      setError(upperFirst(t('errors:notPossibleToEditPaymentMethod')));
    } finally {
      setIsSavingData(false);
    }
  };

  const closeModal = () => {
    reset();
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <StyledPaymentsListItem>
      <StyledPaymentsListItemText>
        <strong>{IBAN}</strong>
        <em>{accountHolderName}</em>
        <em>{bankName}</em>
        <em>{country}</em>
        {isDefault && (
          <StyledPaymentsListItemActive>
            {t('payments:default')} <StyledCheckIcon />
          </StyledPaymentsListItemActive>
        )}
      </StyledPaymentsListItemText>
      <StyledPaymentsListItemCta
        onClick={openModal}
        title={t('form:edit')}
        disabled={!id}
      >
        {t('form:edit')}
      </StyledPaymentsListItemCta>
      <ModalBox
        ariaLabel={t('a11y:close')}
        open={isModalOpen}
        handleCloseModal={closeModal}
      >
        <StyledModalInnerWrapper>
          <StyledModalHeader>
            <Heading sm="20px" md="20px" lg="24px" tag="h1">
              {t('payments:editBankAccount')}
            </Heading>
          </StyledModalHeader>
          <form
            id="payment_method_form"
            onSubmit={handleSubmit(handleFormSubmit)}
            noValidate
          >
            <StyledModalFieldset>
              <StyledModalFieldList>
                <StyledModalFieldListItem>
                  <TextInput
                    disabled
                    id="IBAN"
                    label="IBAN"
                    name="IBAN"
                    type="text"
                    value={IBAN}
                  />
                </StyledModalFieldListItem>
                <StyledModalFieldListItem>
                  <Controller
                    control={control}
                    name="accountHolderName"
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextInput
                        id="name"
                        label={t('payments:accountHolderName')}
                        name="name"
                        type="text"
                        value={value}
                        onChange={debounce(onChange, 300)}
                      >
                        {formState.errors.accountHolderName && (
                          <FormNotice isWrapped={true}>
                            {t('form:requiredField')}
                          </FormNotice>
                        )}
                      </TextInput>
                    )}
                  />
                </StyledModalFieldListItem>
                <StyledModalFieldListItem>
                  <StyledLabel>{t('payments:default')}</StyledLabel>
                  <Controller
                    control={control}
                    name="isDefault"
                    render={({ field: { onChange, value } }) => (
                      <SwitchToggle
                        checked={value}
                        id="active"
                        width={56}
                        height={28}
                        handleDiameter={16}
                        onColor={theme.colors.cross.greenMint}
                        offColor={theme.colors.cross.grayson}
                        onChange={onChange}
                        isDisabled={isDefault}
                      />
                    )}
                  />
                </StyledModalFieldListItem>
              </StyledModalFieldList>
            </StyledModalFieldset>
            <StyledModalFooter>
              {error && !isSavingData && <StyledError>{error}</StyledError>}
              <CtaButton
                disabled={isSavingData}
                title={t('a11y:saveAndClose')}
                type="submit"
                user="landlord"
              >
                {isSavingData ? (
                  <StyledModalSpinnerWrapper>
                    <Spinner
                      backgroundColor={rgba(theme.colors.main, 0.1)}
                      foregroundColor={theme.colors.main}
                      size={14}
                      strokeSize={2}
                    />
                  </StyledModalSpinnerWrapper>
                ) : (
                  toUpper(t('a11y:saveAndClose'))
                )}
              </CtaButton>
            </StyledModalFooter>
          </form>
        </StyledModalInnerWrapper>
      </ModalBox>
    </StyledPaymentsListItem>
  );
};

export default UserPaymentsBankAccountsItem;
